import { FC, useEffect, useRef, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { ArrowBack } from '@mui/icons-material'
import { Box, Button, Card, CardContent, Checkbox, FormControlLabel, Grid, Skeleton, Stack, Typography } from '@mui/material'

import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import { ConfirmDialogData } from '../../../types/ConfirmDialogData'
import { useGetInternalAnalystsQuery } from '../../api/analysts'
import { useGetInternalGameDocumentQuery } from '../../api/games'
import { stillStoredError, useDeleteAnalysisCommentMutation, useGetInternalAnalysisCommentQuery, useSaveAnalysisCommentMutation } from '../../api/reviews'
import { DiscardChangesDialog } from '../../components/DiscardChangesDialog/DiscardChangesDialog'
import { InputTranslator } from '../../components/InputTranslator/InputTranslator'
import { QueryStateBoundary } from '../../components/QueryStateBoundary/QueryStateBoundary'
import { AnalystPicker } from '../../features/review/components/AnalystPicker/AnalystPicker'
import { ReviewGameHeader } from '../../features/review/components/ReviewGameHeader/ReviewGameHeader'
import { ReviewPreview } from '../../features/review/components/ReviewPreview/ReviewPreview'
import {
  AnalysisCommentFormValues,
  emptyTranslation,
  getAnalysisCommentEditPath,
  getEditorLeavePath,
  getUnstoredAnalysisCommentFields,
  needsPublishConfirmation,
  storedFieldLabelKeys,
  toAnalysisCommentFormValues,
  toAnalysisCommentMutation,
} from '../../features/review/helpers/reviewHelpers'
import { useEditPageLeave } from '../../hooks/useEditPageLeave'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'
import { useScreenshotLinkEditorConfig } from '../../hooks/useScreenshotLinkEditorConfig'
import { InputTranslatorType } from '../../types/InputTranslatorType'

type PageAction = 'publish' | 'delete'

// No <form> here: the InputTranslator dialog has its own form, and React bubbles its submit through the portal into any parent form.
export const AnalysisCommentEditPage: FC = () => {
  const { gameId = '', commentId } = useParams<{ gameId: string; commentId?: string }>()
  const [searchParams] = useSearchParams()
  const analysisId = searchParams.get('analysisId')
  usePage(pageService.getInternalPageWithId('internal-analysis-comment-edit'))
  const { t } = useTranslation()
  const { notifySuccess, notifyError } = useMutationFeedback()
  const navigate = useNavigate()
  const gameQuery = useGetInternalGameDocumentQuery(gameId, { skip: !gameId })
  const commentQuery = useGetInternalAnalysisCommentQuery(commentId ?? '', { skip: !commentId })
  const analystsQuery = useGetInternalAnalystsQuery()
  const [saveComment, { isLoading: isSaving }] = useSaveAnalysisCommentMutation()
  const [deleteComment, { isLoading: isDeleting }] = useDeleteAnalysisCommentMutation()
  const [confirmDialogData, setConfirmDialogData] = useState<ConfirmDialogData<PageAction>>()
  const {
    control,
    reset,
    getValues,
    formState: { isDirty },
  } = useForm<AnalysisCommentFormValues>({ defaultValues: toAnalysisCommentFormValues(undefined) })
  const values = useWatch({ control }) as AnalysisCommentFormValues
  const { guard, leave } = useEditPageLeave(isDirty)
  const game = gameQuery.data
  const comment = commentId ? commentQuery.data ?? undefined : undefined
  const gameVersion = comment?.gameVersion || searchParams.get('gameVersion') || ''
  const editorConfig = useScreenshotLinkEditorConfig(game?.id)
  const loadedCommentId = useRef<string>()

  // Only on the first load of a comment, so a refetch can't replace edits that weren't saved.
  useEffect(() => {
    if (comment && loadedCommentId.current !== comment.id) {
      loadedCommentId.current = comment.id
      reset(toAnalysisCommentFormValues(comment))
    }
  }, [comment, reset])

  const save = async () => {
    if (!game?.id || !gameVersion) return
    const body = toAnalysisCommentMutation(comment, game.id, gameVersion, getValues())
    try {
      const stored = await saveComment(body).unwrap()
      loadedCommentId.current = stored.id
      reset(toAnalysisCommentFormValues(stored))
      const unstored = getUnstoredAnalysisCommentFields(body, stored)
      if (unstored.length)
        notifyError(t('internal-analysts:comment_not_stored', { fields: unstored.map((field) => t(storedFieldLabelKeys[field])).join(', ') }))
      else notifySuccess(t('internal-analysts:comment_saved'))
      if (stored.id !== commentId || searchParams.get('gameVersion') !== stored.gameVersion)
        leave(getAnalysisCommentEditPath({ gameId, gameVersion: stored.gameVersion, commentId: stored.id, analysisId }), { replace: true })
    } catch {
      notifyError(t('internal-analysts:comment_save_failed'))
    }
  }

  const remove = async () => {
    if (!comment) return
    try {
      await deleteComment({ id: comment.id, gameId: comment.gameId }).unwrap()
      notifySuccess(t('internal-analysts:comment_deleted'))
      leave(getEditorLeavePath(analysisId))
    } catch (error) {
      const stillStored = (error as { error?: string } | undefined)?.error === stillStoredError
      notifyError(stillStored ? t('internal-analysts:comment_still_stored') : t('internal-analysts:comment_delete_failed'))
    }
  }

  const handleSave = () => {
    if (!needsPublishConfirmation(comment, getValues())) {
      save()
      return
    }
    setConfirmDialogData({
      title: t('internal-analysts:comment_publish_title'),
      confirmText: t('internal-analysts:comment_publish_confirm', { name: game?.name, version: gameVersion }),
      actionText: t('internal-analysts:review_publish'),
      data: 'publish',
    })
  }

  const handleDelete = () =>
    setConfirmDialogData({
      title: t('internal-analysts:comment_delete_title'),
      confirmText: t('internal-analysts:comment_delete_confirm', { name: game?.name, version: gameVersion }),
      actionText: t('internal-analysts:review_delete'),
      destructiveAction: true,
      data: 'delete',
    })

  const handleConfirm = (confirmed?: ConfirmDialogData<PageAction>) => {
    setConfirmDialogData(undefined)
    if (confirmed?.data === 'publish') save()
    if (confirmed?.data === 'delete') remove()
  }

  const isLoading = gameQuery.isLoading || (!!commentId && commentQuery.isLoading)
  const loadError = gameQuery.isError
    ? t('internal-analysts:review_game_load_failed')
    : !!commentId && commentQuery.isError
    ? t('internal-analysts:comment_load_failed')
    : !!commentId && commentQuery.isSuccess && !commentQuery.data
    ? t('internal-analysts:comment_not_found')
    : comment && game && comment.gameId !== game.id
    ? t('internal-analysts:comment_other_game', { gameId: comment.gameId })
    : !gameVersion && !gameQuery.isLoading
    ? t('internal-analysts:comment_needs_version')
    : undefined
  const isBusy = isSaving || isDeleting
  const analyst = analystsQuery.data?.find(({ id }) => id === values.analystId)

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button startIcon={<ArrowBack />} onClick={() => navigate(getEditorLeavePath(analysisId))}>
            {t('internal-common:back')}
          </Button>
          <Typography variant="h5" fontWeight="bold">
            {t('internal-analysts:comment_title')}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          {comment && (
            <Button color="error" onClick={handleDelete} disabled={isLoading || !!loadError || isBusy}>
              {t('internal-common:delete')}
            </Button>
          )}
          <Button variant="contained" onClick={handleSave} disabled={isLoading || !!loadError || isBusy || !game?.id || !gameVersion}>
            {t('internal-common:save')}
          </Button>
        </Stack>
      </Stack>

      <QueryStateBoundary isLoading={isLoading} isError={!!loadError} errorText={loadError} skeletonHeight={360}>
        {game && (
          <Grid container spacing={3} wrap="wrap">
            <Grid item xs={12} md={7}>
              <Card>
                <CardContent>
                  <Stack spacing={3}>
                    <ReviewGameHeader game={game} detail={t('internal-analysts:comment_version', { version: gameVersion })} />
                    <Controller
                      name="analystId"
                      control={control}
                      render={({ field }) => <AnalystPicker value={field.value} onChange={(id) => field.onChange(id)} disabled={isBusy} />}
                    />
                    {editorConfig ? (
                      <Controller
                        name="comment"
                        control={control}
                        render={({ field }) => (
                          <InputTranslator
                            value={field.value ?? emptyTranslation}
                            onChange={field.onChange}
                            inputType={InputTranslatorType.WYSIWYG}
                            label={t('internal-analysts:comment_comment')}
                            dialogTitle={t('internal-analysts:comment_comment')}
                            wysiwygConfig={editorConfig}
                            disabled={isBusy}
                          />
                        )}
                      />
                    ) : (
                      <Skeleton variant="rectangular" height={56} />
                    )}
                    <Box>
                      <Controller
                        name="published"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={<Checkbox checked={field.value} onChange={(event) => field.onChange(event.target.checked)} disabled={isBusy} />}
                            label={t('internal-analysts:review_published')}
                          />
                        )}
                      />
                      {comment?.published && (
                        <Typography variant="body2" color="text.secondary">
                          {t('internal-analysts:comment_published_help')}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box sx={{ position: 'sticky', top: (theme) => theme.spacing(2) }}>
                <ReviewPreview
                  title={t('internal-analysts:comment_preview_title')}
                  subtitle={t('internal-analysts:comment_preview_subtitle')}
                  analyst={analyst}
                  sections={[{ key: 'comment', html: values.comment?.en }]}
                />
              </Box>
            </Grid>
          </Grid>
        )}
      </QueryStateBoundary>

      <ConfirmDialog open={!!confirmDialogData} confirmDialogData={confirmDialogData} onClose={handleConfirm} />
      <DiscardChangesDialog
        guard={guard}
        title={t('internal-analysts:review_discard_title')}
        text={t('internal-analysts:review_discard_confirm')}
        discardText={t('internal-analysts:review_discard')}
      />
    </Box>
  )
}
